import React, { useState, useEffect, useRef, Suspense, useMemo } from "react";
import { Tabs, Tab, Spinner } from "react-bootstrap";
import NewsCard from "./NewsCard";
import "./News.css";
import { buildNewsCardTranslationData } from "../../lib/firestoreTranslations";
import EditionDateBreadcrumb from "./EditionDateBreadcrumb";

// Lazy load components for better performance
const Games = React.lazy(() => import("./Games/Games"));
const StoryPlayer = React.lazy(() => import("./Convo/StoryPlayer"));

/** Set true to show the StoryPlayer tab again. */
const SHOW_CONVERSATION_TAB = false;

// Loading fallback for lazy components
const LazyLoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
    <Spinner animation="border" role="status" />
  </div>
);

const TabsContainer = ({
  newsSummaries,
  selectedLanguage,
  gameWords,
  onTranslationsReady,
  archiveDateKeys,
  selectedDateKey,
  londonToday,
  onSelectDateKey,
  editionLoading,
}) => {
  const [subjects, setSubjects] = useState([]);
  const onTranslationsReadyRef = useRef(onTranslationsReady);
  onTranslationsReadyRef.current = onTranslationsReady;
  const newsRef = useRef(null);
  const gamesRef = useRef(null);
  const gamesTabsScrollRef = useRef(null);
  const [activeTab, setActiveTab] = useState('news');

  const translatedNews = useMemo(() => {
    if (!newsSummaries?.length || !selectedLanguage) return [];
    return newsSummaries.map((news) => ({
      ...news,
      translationData: buildNewsCardTranslationData(news, selectedLanguage),
    }));
  }, [newsSummaries, selectedLanguage]);

  useEffect(() => {
    setSubjects(newsSummaries ? [...new Set(newsSummaries.map((news) => news.subject))] : []);
  }, [newsSummaries]);

  useEffect(() => {
    if (newsSummaries?.length && selectedLanguage) {
      onTranslationsReadyRef.current?.();
    }
  }, [newsSummaries, selectedLanguage]);

  const handleTabSelect = (key) => {
    setActiveTab(key);
    setTimeout(() => {
      if (key === 'news' && newsRef.current) newsRef.current.scrollIntoView({ behavior: 'smooth' });
      if (key === 'games' && gamesTabsScrollRef.current) gamesTabsScrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100); // slight delay to ensure content is rendered
  };

  return (
    <>
      <style>
        {`
          /* General Tab Styling */
          #news-tabs {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
          }
          .nav-tabs {
            width: 100%;
            border-bottom: 2px solid transparent;
          }
          .nav-item {
            margin-right: 20px;
          }
          .nav-link {
            font-weight: bold;
            color: black;
            padding: 10px 20px;
            text-align: center;
            border: none;
            outline: none;
            background: none;
            transition: background 0.3s ease, color 0.3s ease;
            width: 100%;
            display: flex;
            justify-content: center;
            border-radius: 0;
          }
          .nav-link:hover {
            background-color: #f0f0f0;
            color: black !important;
            border-radius: 0
          }
          .nav-tabs .nav-link.active {
            color: red !important;
            border-bottom: 4px solid red !important;
            border-left: 1px solid white !important;
            border-right: 1px solid white !important;
            border-top: 1px solid white !important;
            border-radius: 0
          }
          .nav-link:focus {
            box-shadow: none;
          }
        `}
      </style>
      <Tabs defaultActiveKey="news" id="news-tabs" className="mb-3" activeKey={activeTab} onSelect={handleTabSelect}>
        <Tab eventKey="news" title="News">
          <div ref={newsRef} style={{ borderTop: '1px solid lightGrey'}}><br/></div>
          <EditionDateBreadcrumb
            dateKeys={archiveDateKeys}
            selectedDateKey={selectedDateKey}
            londonToday={londonToday}
            onSelectDateKey={onSelectDateKey}
            disabled={editionLoading}
          />
          <div className="news-articles-list">
            {translatedNews.map((news, index) => (
              <NewsCard
                key={index}
                news={news}
                selectedLanguage={selectedLanguage}
                translationData={news.translationData}
              />
            ))}
          </div>
          </Tab>
        <Tab eventKey="games" title="Games">
          <div ref={gamesRef} style={{ borderTop: '1px solid lightGrey'}}><br/></div>
          <Suspense fallback={<LazyLoadingFallback />}>
            <Games newsSummaries={newsSummaries} selectedLanguage={selectedLanguage} gameWords={gameWords} scrollTargetRef={gamesTabsScrollRef} />
          </Suspense>
        </Tab>
        {SHOW_CONVERSATION_TAB && (
          <Tab eventKey="conversation" title="Conversation">
            <div style={{ borderTop: '1px solid lightGrey'}}><br/></div>
            <Suspense fallback={<LazyLoadingFallback />}>
              <StoryPlayer />
            </Suspense>
          </Tab>
        )}
      </Tabs>
    </>
  );
};

export default TabsContainer;
